import React, { type PropsWithChildren } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

import { radii } from '../../../core/theme/tokens';
import { glassTokens, type GlassTint } from './glassTokens';

export type GlassSurfaceProps = PropsWithChildren<{
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  tint?: GlassTint;
  rounded?: keyof typeof radii | 'none';
  showHairline?: boolean;
}>;

/** Web frosted glass — CSS backdrop-filter + inset highlight. */
export function GlassSurface({
  children,
  style,
  contentStyle,
  tint = 'neutral',
  rounded = 'lg',
  showHairline = false,
}: GlassSurfaceProps) {
  const token = glassTokens[tint];
  const radius = rounded === 'none' ? 0 : radii[rounded];
  // More opaque than native glass so stacked/transition frames don't show through.
  const fill =
    tint === 'neutral'
      ? 'rgba(11, 15, 20, 0.92)'
      : tint === 'tabBar'
        ? 'rgba(11, 15, 20, 0.94)'
        : token.fill;

  return (
    <View
      style={[
        styles.shell,
        {
          borderRadius: radius,
          backgroundColor: fill,
          borderColor: token.border,
        },
        style,
      ]}
    >
      {showHairline ? (
        <View style={[styles.hairline, { backgroundColor: token.hairline }]} pointerEvents="none" />
      ) : null}
      <View style={[styles.content, contentStyle]}>{children}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    overflow: 'hidden',
    borderWidth: 1,
    // Slightly heavier blur is expensive — keep moderate; isolation helps contain paint.
    backdropFilter: 'blur(16px) saturate(150%)',
    WebkitBackdropFilter: 'blur(16px) saturate(150%)',
    isolation: 'isolate',
    boxShadow:
      '0 8px 32px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
  } as object,
  hairline: {
    position: 'absolute',
    top: 0,
    left: 10,
    right: 10,
    height: StyleSheet.hairlineWidth,
    zIndex: 1,
  },
  content: {
    position: 'relative',
    zIndex: 2,
    flexDirection: 'column',
  },
});
